import React, { useEffect, useState, useRef } from "react";
import api from "../../api/axios";
import ConfirmAttendanceMap from "./ConfirmAttendanceMap";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import { MAPLABELS } from "../../constants/maplabels";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";
import {
  getGeolocationErrorKind,
  isAcceptableAttendancePosition,
  isReusableAttendancePosition,
  openGeolocationSettings,
  requestBestEffortGeolocation,
} from "../../utils/attendanceGeolocation";
import {
  getLocationPermissionState,
  toPublicWebUrl,
} from "../../native/runtime";
import { EVENT_ATTENDANCE_OPENS_BEFORE_MS } from "../../hooks/useEventAttendanceWindow";
import {
  fetchSportSelfAssessment,
  saveSportSelfAssessment,
} from "../../api/roulette";

/**
 * ConfirmAttendanceModal
 *
 * Focus: сделать повторный запрос геолокации надёжным и быстрым,
 * без длинных инструкций. Если браузер блокирует доступ (PERMISSION_DENIED),
 * показываем короткую ошибку и даём мгновенный ретрай (кнопкой «Try again»).
 * Для таймаутов/недоступности включаем watchPosition-fallback.
 */
const ConfirmAttendanceModal = ({
  visible = false,
  eventId,
  onClose,
  onSuccess,
  onRequirePayment,
  initialMode = "geo",
  forceHostQr = false,
  cashPaymentQrMode = false,
}) => {
  const { t } = useTranslation("events");

  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  // idle → locating → pending → success | error
  const [submitState, setSubmitState] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [focusUserLocation, setFocusUserLocation] = useState(false);
  const [geoSettingsCtaVisible, setGeoSettingsCtaVisible] = useState(false);
  const [showPeerHelpHint, setShowPeerHelpHint] = useState(false);
  const [skillProfile, setSkillProfile] = useState(null);
  const [skillCheckState, setSkillCheckState] = useState("idle");
  const [skillSavingLevel, setSkillSavingLevel] = useState(null);
  const [skillError, setSkillError] = useState("");
  // Mode: 'geo' (default) or 'host' (QR by host)
  const [mode, setMode] = useState(
    forceHostQr || cashPaymentQrMode || initialMode === "host" ? "host" : "geo"
  );
  const [confirmedByHost, setConfirmedByHost] = useState(false);
  const successNotifiedRef = useRef(false);
  const successCloseTimerRef = useRef(null);
  const locationRequestRef = useRef(null);

  // QR mode state
  const [qrLoading, setQrLoading] = useState(false);
  const [qrToken, setQrToken] = useState(null);
  const [qrClaimPath, setQrClaimPath] = useState(null);
  const [qrExpiresAt, setQrExpiresAt] = useState(null); // epoch ms
  const [qrError, setQrError] = useState("");
  const qrCanvasRef = useRef(null);

  const requestAttendancePosition = React.useCallback((options) => {
    if (locationRequestRef.current) return locationRequestRef.current;

    const request = requestBestEffortGeolocation(options);
    locationRequestRef.current = request;
    request.then(
      () => {
        if (locationRequestRef.current === request) {
          locationRequestRef.current = null;
        }
      },
      () => {
        if (locationRequestRef.current === request) {
          locationRequestRef.current = null;
        }
      }
    );
    return request;
  }, []);

  useEffect(() => {
    if (!visible) return;
    successNotifiedRef.current = false;
    setUserCoords((current) => (
      isAcceptableAttendancePosition(current) ? current : null
    ));
    return () => {
      if (successCloseTimerRef.current) {
        window.clearTimeout(successCloseTimerRef.current);
        successCloseTimerRef.current = null;
      }
    };
  }, [visible, eventId]);

  const notifySuccessOnce = React.useCallback((confirmation = null) => {
    if (successNotifiedRef.current) return;
    successNotifiedRef.current = true;
    onSuccess?.(confirmation);
  }, [onSuccess]);

  // Tick every second to update the QR countdown when visible
  const [, setQrTick] = useState(0);
  useEffect(() => {
    if (!qrExpiresAt) return;
    const id = setInterval(() => setQrTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [qrExpiresAt]);

  const qrMsLeft = qrExpiresAt ? Math.max(0, qrExpiresAt - Date.now()) : 0;

  /** timer until start */
  const [timeLeft, setTimeLeft] = useState(null);

  const formatCountdown = (ms) => {
    const totalSec = Math.max(0, Math.ceil(ms / 1000));
    const hrs = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    const pad = (n) => n.toString().padStart(2, "0");
    return hrs > 0
      ? `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
      : `${pad(mins)}:${pad(secs)}`;
  };
  const startTimeStr = eventData?.date_time || eventData?.day_time || null;
  const eventActive =
    timeLeft !== null && timeLeft <= EVENT_ATTENDANCE_OPENS_BEFORE_MS;
  const msUntilOpen =
    timeLeft === null
      ? null
      : Math.max(0, timeLeft - EVENT_ATTENDANCE_OPENS_BEFORE_MS);
  const showButton = eventData?.is_host || eventData?.is_participant;
  const attendanceQrOnly =
    String(eventData?.attendance_confirmation_method || "").trim().toLowerCase() ===
    "host_qr_only";
  // True when this specific participant must use host QR (e.g. Multisport users on multisport_qr_only events)
  const requiresHostQr = !!eventData?.requires_host_qr;
  const geoBlocked = forceHostQr || cashPaymentQrMode || attendanceQrOnly || requiresHostQr;
  const isSportParticipantEvent = Boolean(
    eventData?.is_sport_session_event &&
      eventData?.is_participant &&
      !eventData?.is_host
  );
  const sportSkillGatePending = Boolean(
    isSportParticipantEvent &&
      (skillCheckState !== "ready" || skillProfile?.needs_self_assessment)
  );

  useEffect(() => {
    if (!visible) return;
    setMode(
      forceHostQr || cashPaymentQrMode || initialMode === "host" ? "host" : "geo"
    );
  }, [visible, forceHostQr, cashPaymentQrMode, initialMode]);

  const genQr = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!eventId) return;
      setQrLoading(true);
      setQrError("");
      // Block QR generation until the confirmation window is active
      if (!eventActive) {
        const msg = t("confirm_attendance_modal.qr.not_active");
        setQrError(msg);
        window.dispatchEvent(
          new CustomEvent("global-info", { detail: { message: msg } })
        );
        setQrLoading(false);
        return;
      }
      try {
        const { data } = await api.post(`events/${eventId}/attendance/qr-token/`);
        setQrToken(data.token);
        setQrClaimPath(data.claim_path);
        const expiresAt = Date.now() + data.expires_in * 1000;
        setQrExpiresAt(expiresAt);
        // Render QR into canvas (encode absolute URL)
        const url = toPublicWebUrl(data.claim_path);
        if (qrCanvasRef.current) {
          await QRCode.toCanvas(qrCanvasRef.current, url, {
            width: 256,
            margin: 1,
          });
        }
        if (!silent) {
          window.dispatchEvent(
            new CustomEvent("global-info", {
              detail: {
                message: t("confirm_attendance_modal.qr.generated"),
              },
            })
          );
        }
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          t("confirm_attendance_modal.qr.failed");
        setQrError(msg);
        window.dispatchEvent(
          new CustomEvent("global-error", { detail: { message: msg } })
        );
      } finally {
        setQrLoading(false);
      }
    },
    [eventId, eventActive, t]
  );

  const handleCopyClaim = async () => {
    if (!qrClaimPath) return;
    try {
      const abs = toPublicWebUrl(qrClaimPath);
      await navigator.clipboard.writeText(abs);
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: t("confirm_attendance_modal.qr.copy_success"),
          },
        })
      );
    } catch {
      // no-op
    }
  };

  // Автолокация только если уже выдано разрешение
  useEffect(() => {
    if (
      !visible ||
      !eventActive ||
      !eventData ||
      geoBlocked ||
      mode !== "geo" ||
      userCoords ||
      sportSkillGatePending
    ) return;
    let cancelled = false;
    (async () => {
      try {
        const state = await getLocationPermissionState();
        if (!["granted", "limited"].includes(state)) return;
        const position = await requestAttendancePosition();
        if (!cancelled) setUserCoords(position);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [
    visible,
    eventActive,
    eventData,
    geoBlocked,
    mode,
    userCoords,
    sportSkillGatePending,
    requestAttendancePosition,
  ]);

  const loadSportSkillProfile = React.useCallback(async () => {
    if (!eventId) return null;
    setSkillCheckState("loading");
    setSkillError("");
    try {
      const data = await fetchSportSelfAssessment(eventId);
      const profile = data?.skill_profile || null;
      if (!profile) {
        throw new Error(t("confirm_attendance_modal.skill_level.load_failed"));
      }
      setSkillProfile(profile);
      setSkillCheckState("ready");
      return profile;
    } catch (err) {
      setSkillCheckState("error");
      setSkillError(
        err?.response?.data?.error ||
          t("confirm_attendance_modal.skill_level.load_failed")
      );
      return null;
    }
  }, [eventId, t]);

  /** fetch event on open */
  useEffect(() => {
    if (!visible || !eventId) return;
    const fetchEvent = async () => {
      setLoading(true);
      setSkillProfile(null);
      setSkillCheckState("loading");
      setSkillSavingLevel(null);
      setSkillError("");
      try {
        setEventData(null);
        setErrorMsg("");
        const { data } = await api.get(`events/event-location/${eventId}/`);
        const label =
          MAPLABELS.find((l) => l.id === data.map_label) || MAPLABELS[0];
        setEventData({
          id: data.id,
          name: data.name,
          latitude: data.latitude,
          longitude: data.longitude,
          dotSize: 8,
          dotColor: label.dotColor,
          icon: data.map_label,
          iconSrc: label.src,
          day_time: data.DayTime ?? data.day_time ?? null,
          date_time: data.date_time ?? null,
          allowed_radius: data.allowed_radius,
          is_host: data.is_host,
          is_participant: data.is_participant,
          is_sport_session_event: !!data.is_sport_session_event,
          attendance_confirmation_method: data.attendance_confirmation_method || "geo_or_qr",
          requires_host_qr: !!data.requires_host_qr,
        });
        if (
          (data.attendance_confirmation_method || "geo_or_qr") === "host_qr_only" ||
          !!data.requires_host_qr
        ) {
          setMode("host");
        }
        if (
          data.is_sport_session_event &&
          data.is_participant &&
          !data.is_host
        ) {
          await loadSportSkillProfile();
        } else {
          setSkillCheckState("ready");
        }
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          t("confirm_attendance_modal.geo_errors.load_failed");
        setErrorMsg(msg);
        window.dispatchEvent(
          new CustomEvent("global-error", { detail: { message: msg } })
        );
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [visible, eventId, loadSportSkillProfile, t]);

  // Тикаем обратный отсчёт
  useEffect(() => {
    if (!startTimeStr) return;
    const update = () => setTimeLeft(new Date(startTimeStr) - Date.now());
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [startTimeStr]);

  // Listen for real-time attendance confirmation via WebSocket notification
  useEffect(() => {
    if (!visible || !eventId) return;
    const handler = (e) => {
      const notif = e.detail;
      const meta = notif?.meta || {};
      if (
        String(meta.action) === "attendance_confirmed" &&
        String(meta.event_id) === String(eventId)
      ) {
        setConfirmedByHost(true);
        notifySuccessOnce(meta);
      }
    };
    window.addEventListener("vg:notification", handler);
    return () => window.removeEventListener("vg:notification", handler);
  }, [visible, eventId, notifySuccessOnce]);

  // Auto-generate QR when user opens host mode and check-in window is already active.
  useEffect(() => {
    if (!visible || mode !== "host" || !eventActive || sportSkillGatePending) return;
    if (qrLoading || qrToken || qrError) return;
    void genQr({ silent: true });
  }, [
    visible,
    mode,
    eventActive,
    sportSkillGatePending,
    qrLoading,
    qrToken,
    qrError,
    genQr,
  ]);

  const handleSaveSportSkill = async (level) => {
    if (!eventId || skillSavingLevel != null) return;
    setSkillSavingLevel(level);
    setSkillError("");
    try {
      const data = await saveSportSelfAssessment(eventId, level);
      const profile = data?.skill_profile || null;
      if (!profile || profile.needs_self_assessment) {
        throw new Error(t("confirm_attendance_modal.skill_level.save_failed"));
      }
      setSkillProfile(profile);
      setSkillCheckState("ready");
    } catch (err) {
      setSkillError(
        err?.response?.data?.error ||
          err?.message ||
          t("confirm_attendance_modal.skill_level.save_failed")
      );
    } finally {
      setSkillSavingLevel(null);
    }
  };

  const mapGeoErrorToMessage = (error) => {
    const kind = getGeolocationErrorKind(error);
    switch (kind) {
      case "permission_denied":
        return t("confirm_attendance_modal.geo_errors.blocked");
      case "telegram_permission_denied":
        return t("confirm_attendance_modal.geo_errors.telegram_blocked");
      case "position_unavailable":
        return t("confirm_attendance_modal.geo_errors.position_unavailable");
      case "timeout":
        return t("confirm_attendance_modal.geo_errors.timeout");
      case "insecure_context":
        return t("confirm_attendance_modal.geo_errors.insecure_context");
      case "telegram_unavailable":
        return t("confirm_attendance_modal.geo_errors.telegram_unavailable");
      case "not_supported":
        return t("confirm_attendance_modal.geo_errors.not_supported");
      default:
        return error?.message || t("confirm_attendance_modal.geo_errors.generic");
    }
  };

  const handleOpenGeoSettings = () => {
    if (!openGeolocationSettings()) return;
    window.dispatchEvent(
      new CustomEvent("global-info", {
        detail: {
          message: t("confirm_attendance_modal.geo_errors.opened_settings"),
        },
      })
    );
  };

  /** POST /confirm */
  const handleConfirm = async () => {
    if (!eventId || (submitState !== "idle" && submitState !== "error")) return;
    try {
      setShowPeerHelpHint(false);
      setGeoSettingsCtaVisible(false);
      setFocusUserLocation(false);
      let coords = isReusableAttendancePosition(userCoords, eventData)
        ? userCoords
        : null;
      if (!coords) {
        setErrorMsg("");
        setSubmitState("locating");
        const bypassCache =
          isAcceptableAttendancePosition(userCoords) && userCoords?.isCached;
        coords = await requestAttendancePosition(
          bypassCache ? { cacheMaxAgeMs: 0 } : undefined
        );
        if (!isReusableAttendancePosition(coords, eventData) && coords?.isCached) {
          coords = await requestAttendancePosition({ cacheMaxAgeMs: 0 });
        }
        setUserCoords(coords);
      }
      setSubmitState("pending");
      const { data: confirmResp } = await api.post(`events/confirm_attendance/${eventId}/`, {
        latitude: Number(coords.latitude),
        longitude: Number(coords.longitude),
      });
      const canHelpOthersByQr = Boolean(confirmResp?.can_help_others_by_qr);
      const opensInteractiveSession = Boolean(confirmResp?.party_session_path);
      setSubmitState("success");
      // The session opens immediately after interactive-event check-in, so do
      // not expand the footer and crop the map with the peer-help hint first.
      setShowPeerHelpHint(canHelpOthersByQr && !opensInteractiveSession);
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: t("confirm_attendance_modal.geo_errors.success"),
          },
        })
      );
      notifySuccessOnce(confirmResp);
      if (!opensInteractiveSession) {
        successCloseTimerRef.current = window.setTimeout(() => {
          successCloseTimerRef.current = null;
          setSubmitState("idle");
          onClose();
        }, canHelpOthersByQr ? 15000 : 1200);
      }
    } catch (err) {
      setShowPeerHelpHint(false);
      console.warn("[ConfirmAttendanceModal] geolocation confirmation failed", {
        kind: getGeolocationErrorKind(err),
        code: err?.code,
        source: err?.source,
        permissionState: err?.permissionState,
        env: err?.env,
      });
      // Prefer detailed API error from backend, then fall back to generic messages
      const apiErr = err?.response?.data;
      let msg = null;

      // If backend says payment is required, close this modal and open the payment/intention modal instead
      if (apiErr?.code && String(apiErr.code).startsWith("payment_required")) {
        msg =
          apiErr.error ||
          t("confirm_attendance_modal.geo_errors.payment_required");
        window.dispatchEvent(
          new CustomEvent("global-error", { detail: { message: msg } })
        );
        // Reset local state and delegate to parent to open payment modal
        setSubmitState("idle");
        setErrorMsg("");
        onClose?.();
        onRequirePayment?.();
        return;
      }

      if (apiErr?.error) {
        if (apiErr.code === "attendance_qr_only") {
          msg = apiErr.error;
          setMode("host");
        } else if (apiErr.code === "outside_radius") {
          const dist = Math.round(apiErr.distance_m ?? 0);
          const allowed =
            apiErr.allowed_radius_m ?? eventData?.allowed_radius ?? 0;
          setFocusUserLocation(true);
          msg = t("confirm_attendance_modal.geo_errors.outside_radius", {
            distance: dist,
            allowed,
          });
        } else if (apiErr.code === "invalid_coords") {
          msg = t("confirm_attendance_modal.geo_errors.invalid_coords", {
            base: apiErr.error,
          });
        } else {
          msg = apiErr.error;
        }
      } else if (err?.request && !err?.response) {
        msg = t("confirm_attendance_modal.geo_errors.network");
      } else {
        msg = mapGeoErrorToMessage(err);
      }

      setGeoSettingsCtaVisible(
        getGeolocationErrorKind(err) === "telegram_permission_denied"
      );
      setErrorMsg(msg);
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg } })
      );
      setSubmitState("error");
    }
  };

  const confirmDisabled =
    !eventActive ||
    submitState === "locating" ||
    submitState === "pending" ||
    submitState === "success";

  const confirmLabels = {
    idle: t("confirm_attendance_modal.buttons.confirm_idle"),
    locating: t("confirm_attendance_modal.buttons.confirm_locating"),
    pending: t("confirm_attendance_modal.buttons.confirm_pending"),
    success: t("confirm_attendance_modal.buttons.confirm_success"),
    error: t("confirm_attendance_modal.buttons.confirm_error"),
  };

  const qrActionButtonClass = `w-full h-12 rounded-xl font-semibold ${
    qrLoading
      ? "bg-white/20 text-gray-400 cursor-wait"
      : !eventActive
      ? "bg-white bg-opacity-20 text-gray-500 cursor-not-allowed"
      : "bg-white text-blue-600"
  }`;

  const geoFooter = showButton && mode === "geo" ? (
    <div className="space-y-3">
      <AppModalButton
        onClick={handleConfirm}
        disabled={confirmDisabled}
        loading={submitState === "pending" || submitState === "locating"}
        variant={submitState === "success" ? "secondary" : "primary"}
        className={
          submitState === "success"
            ? "border-emerald-300/45 !bg-emerald-500/90 !text-white disabled:!bg-emerald-500/90 disabled:!text-white"
            : "text-[#2563EB]"
        }
      >
        {confirmLabels[submitState]}
      </AppModalButton>
      {submitState === "success" && showPeerHelpHint && (
        <div className="rounded-xl border border-white/25 bg-white/10 p-3">
          <p className="text-white text-sm md:text-base text-center font-semibold leading-snug">
            {t("confirm_attendance_modal.helper_after_geo.title")}
          </p>
          <p className="mt-1 text-white/90 text-xs md:text-sm text-center leading-snug">
            {t("confirm_attendance_modal.helper_after_geo.body")}
          </p>
        </div>
      )}
    </div>
  ) : null;

  const skillOptions = [
    {
      level: 1,
      label: t("confirm_attendance_modal.skill_level.beginner"),
      description: t("confirm_attendance_modal.skill_level.beginner_description"),
    },
    {
      level: 2,
      label: t("confirm_attendance_modal.skill_level.intermediate"),
      description: t("confirm_attendance_modal.skill_level.intermediate_description"),
    },
    {
      level: 3,
      label: t("confirm_attendance_modal.skill_level.advanced"),
      description: t("confirm_attendance_modal.skill_level.advanced_description"),
    },
  ];
  const showSkillPreflight = sportSkillGatePending;

  return (
    <AppModalShell
      open={visible}
      title={
        showSkillPreflight
          ? t("confirm_attendance_modal.skill_level.title")
          : t("confirm_attendance_modal.titles.main")
      }
      description={
        showSkillPreflight
          ? t("confirm_attendance_modal.skill_level.description")
          : geoBlocked
          ? t("confirm_attendance_modal.subtitles.qr_only")
          : t("confirm_attendance_modal.subtitles.desktop")
      }
      onClose={onClose}
      closeAriaLabel={t("confirm_attendance_modal.buttons.close_aria")}
      zIndexClassName="z-[13000]"
      maxWidthClassName="max-w-[32rem]"
      descriptionClassName="text-center"
      footer={showSkillPreflight ? null : geoFooter}
      bodyClassName="!px-4 !pb-3 !pt-1 sm:!px-6 sm:!py-4"
    >
      {showSkillPreflight ? (
        <div
          className="space-y-3 pb-2"
          aria-busy={skillCheckState === "loading" || skillSavingLevel != null}
        >
          {skillCheckState === "loading" ? (
            <p className="py-8 text-center text-sm font-semibold text-white/70">
              {t("confirm_attendance_modal.skill_level.loading")}
            </p>
          ) : skillCheckState === "error" ? (
            <div className="space-y-4 py-3">
              <p className="text-center text-sm font-semibold text-red-300">
                {skillError}
              </p>
              <AppModalButton variant="secondary" onClick={loadSportSkillProfile}>
                {t("confirm_attendance_modal.skill_level.retry")}
              </AppModalButton>
            </div>
          ) : (
            <>
              {skillOptions.map((option) => {
                const savingThisLevel = skillSavingLevel === option.level;
                return (
                  <button
                    key={option.level}
                    type="button"
                    onClick={() => handleSaveSportSkill(option.level)}
                    disabled={skillSavingLevel != null}
                    className="w-full rounded-2xl border border-white/14 bg-white/[0.08] px-4 py-3.5 text-left transition hover:bg-white/[0.12] active:scale-[0.99] disabled:cursor-wait disabled:opacity-60"
                  >
                    <span className="block text-base font-semibold text-white">
                      {savingThisLevel
                        ? t("confirm_attendance_modal.skill_level.saving")
                        : option.label}
                    </span>
                    <span className="mt-1.5 block text-sm font-medium leading-5 text-white/72">
                      {option.description}
                    </span>
                  </button>
                );
              })}
              {skillError ? (
                <p className="pt-1 text-center text-sm font-semibold text-red-300">
                  {skillError}
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
        {errorMsg && mode === "geo" && (
          <>
            <p className="text-center text-red-400 text-sm">
              {errorMsg}
            </p>
            {geoSettingsCtaVisible && (
              <div className="flex justify-center">
                <button
                  onClick={handleOpenGeoSettings}
                  className="text-xs text-white underline underline-offset-2"
                  type="button"
                >
                  {t("confirm_attendance_modal.buttons.open_settings")}
                </button>
              </div>
            )}
          </>
        )}
        {qrError && mode === "host" && (
          <p className="text-center text-red-400 text-sm">
            {qrError}
          </p>
        )}
        {!geoBlocked && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setMode("geo")}
              className={`h-10 rounded-xl font-semibold transition ${
                mode === "geo"
                  ? "bg-white text-blue-600"
                  : "bg-white/10 text-white"
              }`}
            >
              {t("confirm_attendance_modal.modes.geo")}
            </button>
            <button
              onClick={() => setMode("host")}
              className={`h-10 rounded-xl font-semibold transition ${
                mode === "host"
                  ? "bg-white text-blue-600"
                  : "bg-white/10 text-white"
              }`}
            >
              {t("confirm_attendance_modal.modes.host")}
            </button>
          </div>
        )}

        {mode === "geo" ? (
          <div className="relative h-[clamp(10rem,32dvh,19rem)] w-full overflow-hidden rounded-xl sm:h-[clamp(12rem,36dvh,21rem)]">
            {!eventActive && msUntilOpen !== null && (
              <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-start pt-24 md:pt-28 z-20 pointer-events-none">
                <p className="text-white/90 text-lg md:text-xl font-semibold mb-1">
                  {t("confirm_attendance_modal.countdown.opens_in")}
                </p>
                <p className="text-white text-4xl md:text-5xl font-extrabold tracking-wider">
                  {formatCountdown(msUntilOpen)}
                </p>
                <p className="text-white/90 text-lg md:text-xl mt-1">
                  {t("confirm_attendance_modal.countdown.at_venue")}
                </p>
              </div>
            )}

            {loading ? (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-center text-white/70">
                  {t("confirm_attendance_modal.status.loading")}
                </p>
              </div>
            ) : eventData &&
              typeof eventData.latitude === "number" &&
              !Number.isNaN(eventData.latitude) &&
              typeof eventData.longitude === "number" &&
              !Number.isNaN(eventData.longitude) ? (
              <ConfirmAttendanceMap
                event={eventData}
                allowedRadius={eventData.allowed_radius}
                userCoords={userCoords}
                focusUserLocation={focusUserLocation}
                iconSrc={eventData.iconSrc}
                dotColor={eventData.dotColor}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-center text-white/70">
                  {t("confirm_attendance_modal.status.invalid_coords")}
                </p>
              </div>
            )}
          </div>
        ) : confirmedByHost ? (
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20">
              <svg className="w-10 h-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-xl font-bold text-white text-center">
              {t("confirm_attendance_modal.geo_errors.success")}
            </p>
            <p className="text-sm text-white/70 text-center">
              {t("confirm_attendance_modal.host_confirmed_notice")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center pt-1">
            <div className="relative">
              <canvas
                ref={qrCanvasRef}
                width={256}
                height={256}
                className="bg-white p-2 rounded-lg shadow-md w-[256px] h-[256px] md:w-[256px] md:h-[256px]"
              />
              {!eventActive && msUntilOpen !== null && (
                <div className="absolute inset-0 flex items-center justify-start pt-28 md:pt-32">
                  <div className="text-center select-none w-full">
                    <div className="text-blue-600/90 text-lg md:text-xl font-semibold mb-1">
                      {t("confirm_attendance_modal.countdown.opens_in")}
                    </div>
                    <div className="text-blue-600 font-extrabold text-4xl md:text-5xl tracking-wider">
                      {formatCountdown(msUntilOpen)}
                    </div>
                    <div className="text-blue-600/90 text-lg md:text-xl mt-1">
                      {t("confirm_attendance_modal.countdown.at_venue")}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {qrToken ? (
              <>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                  <button
                    onClick={handleCopyClaim}
                    className="px-3 h-9 rounded-lg bg-white/10 text-white text-sm"
                  >
                    {t("confirm_attendance_modal.qr.copy_button")}
                  </button>
                  {qrExpiresAt && (
                    <span className="text-white/80 text-sm">
                      {t("confirm_attendance_modal.qr.expires_in", {
                        time: formatCountdown(qrMsLeft),
                      })}
                    </span>
                  )}
                </div>
                <button
                  onClick={genQr}
                  disabled={qrLoading || !eventActive}
                  className={`mt-3 ${qrActionButtonClass}`}
                >
                  {qrLoading
                    ? t("confirm_attendance_modal.qr.refreshing")
                    : t("confirm_attendance_modal.qr.refresh")}
                </button>
                <p className="mt-2 text-center text-white/70 hidden md:block text-sm">
                  {t("confirm_attendance_modal.qr.hint_desktop_existing")}
                </p>
                <p className="mt-2 text-center text-white/70 md:hidden text-xs">
                  {t("confirm_attendance_modal.qr.hint_mobile_existing")}
                </p>
              </>
            ) : (
              <>
                <p className="text-white/80 mb-3 mt-3 text-center hidden md:block text-sm">
                  {t("confirm_attendance_modal.qr.hint_desktop_new")}
                </p>
                <p className="text-white/80 mb-3 mt-3 text-center md:hidden text-xs">
                  {t("confirm_attendance_modal.qr.hint_mobile_new")}
                </p>
                <button
                  onClick={genQr}
                  disabled={qrLoading || !eventActive}
                  className={qrActionButtonClass}
                >
                  {qrLoading
                    ? t("confirm_attendance_modal.qr.generating")
                    : t("confirm_attendance_modal.qr.generate")}
                </button>
              </>
            )}
          </div>
        )}
        </div>
      )}
    </AppModalShell>
  );
};

export default ConfirmAttendanceModal;
